import { useTranslation } from 'react-i18next'
import Card from '@/components/ui/Card'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import type { ZakatSilverInput, CalendarType } from '@/types/zakat'
import { Circle } from 'lucide-react'

interface ZakatSilverFormProps {
  input: ZakatSilverInput
  calendar: CalendarType
  onInputChange: (input: ZakatSilverInput) => void
  onCalendarChange: (calendar: CalendarType) => void
  onCalculate: () => void
}

export default function ZakatSilverForm({
  input,
  calendar,
  onInputChange,
  onCalendarChange,
  onCalculate,
}: ZakatSilverFormProps) {
  const { t } = useTranslation()
  const handleInputChange = (field: keyof ZakatSilverInput, value: string) => {
    const numValue = parseFloat(value) || 0
    onInputChange({
      ...input,
      [field]: numValue,
    })
  }

  return (
    <Card>
      <div className="flex items-center gap-3 mb-6">
        <Circle className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('zakat.silver.title')}</h3>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          {t('zakat.yearOfHawl')}
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="hijri"
              checked={calendar === 'hijri'}
              onChange={() => onCalendarChange('hijri')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.hijri')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="gregorian"
              checked={calendar === 'gregorian'}
              onChange={() => onCalendarChange('gregorian')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.gregorian')}</span>
          </label>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <Input
          type="number"
          label={t('zakat.silver.grams')}
          value={input.grams.toString()}
          onChange={(e) => handleInputChange('grams', e.target.value)}
          placeholder={t('zakat.placeholderGrams')}
        />
        <Input
          type="number"
          label={t('zakat.silver.pricePerGram')}
          value={input.pricePerGram.toString()}
          onChange={(e) => handleInputChange('pricePerGram', e.target.value)}
          placeholder={t('zakat.silver.placeholderPrice')}
        />
      </div>

      <a
        href="https://www.goldpricesdubai.com/ar/%d8%a3%d8%b3%d8%b9%d8%a7%d8%b1-%d8%a7%d9%84%d8%b0%d9%87%d8%a8-%d8%a7%d9%84%d9%8a%d9%88%d9%85/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm mb-4 block"
      >
        {t('zakat.silver.knowSilverPrice')}
      </a>

      <Button variant="primary" size="lg" className="w-full" onClick={onCalculate}>
        {t('zakat.calculateYourZakat')}
      </Button>
    </Card>
  )
}

